import { Component, ChangeDetectionStrategy, inject, signal, Input, Output, EventEmitter, afterNextRender } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule, CardModule, AlertModule, SpinnerModule, BadgeModule } from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { TranslationModule } from 'i18n';
import { CurrencyAmountPipe, Logger } from 'shared';
import { lastValueFrom } from 'rxjs';
import { OrderService } from '../../../../../../services/order.service';

interface BankDetails {
  account_holder?: string;
  bank_name?: string;
  account_number?: string;
  ach_routing?: string;
  wire_routing?: string;
  swift?: string;
  iban?: string;
  bank_address?: string;
  currency?: string;
  reference: string;
}

/**
 * Bank transfer (Banküberweisung) confirmation step.
 *
 * Gateway-agnostic and reusable like the other confirmation components. There is
 * NO external gateway: this only publishes the beneficiary details + the order's
 * payment reference (fetched via initiate_payment) so the customer can wire the
 * money manually, and stresses that the amount must be sent fee-free for the
 * recipient (spesenfrei). The order stays awaiting_confirmation until an admin
 * books the received transfer. Emitting onSuccess just advances the wizard to
 * its "pending" state — it does NOT mean the money has arrived.
 */
@Component({
  selector: 'app-bank-transfer-confirmation',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    AlertModule,
    SpinnerModule,
    BadgeModule,
    IconModule,
    TranslationModule,
    CurrencyAmountPipe
  ],
  templateUrl: './bank-transfer-confirmation.component.html',
  styleUrls: ['./bank-transfer-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BankTransferConfirmationComponent {
  @Input() orderId: number | null = null;
  @Input() totalPrice: number = 0;
  @Input() currency: string = 'USD';
  /** Warning shown before cancelling — supplied by the host (translated). */
  @Input() cancelConfirmMessage = 'Cancel this bank transfer? Your order will be cancelled and you will have to start again.';
  @Output() onSuccess = new EventEmitter<any>();
  /** Emitted when the customer deliberately abandons the bank transfer. The host
   *  cancels the order and restarts the wizard — there is no silent "go back to
   *  pick another payment method". */
  @Output() onCancel = new EventEmitter<void>();

  protected logger = new Logger('BankTransferConfirmationComponent');
  private orderService = inject(OrderService);

  initializing = signal<boolean>(true);
  errorMessage = signal<string>('');
  bank = signal<BankDetails | null>(null);

  constructor() {
    afterNextRender(async () => {
      if (!this.orderId) {
        this.errorMessage.set('Order ID is missing');
        this.initializing.set(false);
        return;
      }
      await this.initialize();
    });
  }

  private async initialize(): Promise<void> {
    this.errorMessage.set('');
    this.initializing.set(true);

    try {
      const response = await lastValueFrom(this.orderService.initiatePayment(this.orderId!, 'bank_transfer'));
      const details = response.bank_transfer;
      if (!details || !details.reference) {
        throw new Error('Bank transfer details are not available');
      }
      if (response.currency) this.currency = response.currency;
      this.bank.set(details);
      this.initializing.set(false);
    } catch (err: any) {
      this.logger.error('Bank transfer init error:', err);
      this.errorMessage.set(err?.error?.error || err?.message || 'Failed to load bank transfer details. Please try again.');
      this.initializing.set(false);
    }
  }

  confirmDone(): void {
    // The customer states they have made the transfer. The order remains
    // awaiting_confirmation until an admin books the incoming payment.
    this.onSuccess.emit({ orderId: this.orderId, pending: true });
  }

  // Deliberate cancel with a warning — the ONLY way off this screen other than
  // completing the transfer. Switching to another payment method is not allowed
  // without cancelling first.
  cancel(): void {
    if (!confirm(this.cancelConfirmMessage)) return;
    this.onCancel.emit();
  }

  async retry(): Promise<void> {
    this.errorMessage.set('');
    await this.initialize();
  }
}
